import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import _isEmpty from 'lodash/isEmpty';
import _includes from 'lodash/includes';
import { consultationSelector } from 'redux/selectors/consultation';
import { STRINGS } from 'config';
import { submitPrimaryCareConsultation, submitETriageConsultation } from 'redux/actions/consultation/submit';
import { logMessage } from 'redux/actions/consultation/question';
import { getModals } from 'components/consultation/modal';
import { getHumanReadableDate } from 'common/dates';
import { Endstate as EndstateContainer } from 'styles/blocks/endstate';
import { ErrorETriageModal } from 'components/consultation/endstate/eTriage/error';
import { Incomplete } from './incomplete';
import { Complete } from './complete';
import { Error } from './error';
import { SubmitETriage } from './eTriage/submit';

const { RESPONSE_TYPE, PRODUCT } = STRINGS;
const ENDSTATES_WHICH_REQUIRE_SUBMISSION = [
  RESPONSE_TYPE.ENDSTATE_COMPLETE,
  RESPONSE_TYPE.ENDSTATE_COMPLETE_ADMIN,
  RESPONSE_TYPE.ENDSTATE_COMPLETE_PATIENT_RESPONSE,
  RESPONSE_TYPE.ENDSTATE_COMPLETE_AUTISM_PRE_HEALTH,
  RESPONSE_TYPE.ENDSTATE_COMPLETE_PATIENT_REGISTRATION,
  RESPONSE_TYPE.ENDSTATE_COMPLETE_OP_AANDA_MSK,
];

export function Endstate(props) {
  const {
    isDuplicateSubmissionModalOpen,
    onConfirmDuplicateSubmissionModal,
    isConsultationEndedModalOpen,
    onConfirmConsultationEndedModal,
    onCloseConsultationEndedModal,
    onCloseDuplicateSubmissionModal,
  } = props;
  const mapState = useCallback(state => consultationSelector(state), []);
  const {
    responseType: endstate,
    submissionDetails,
    question,
    error,
    isClinicalReviewMode,
    previous,
    isLoading,
    isForwardNavigation,
    product,
    isConsultationEnded,
    isPopupSurveyAvailableForProduct,
    practice,
    sessionId,
    conditionName,
    consultationId,
  } = useMappedState(mapState);

  const { ConsultationEndedModal, DuplicateSubmissionModal } = useMemo(() => getModals(product), [product]);

  // Potentially deprecated. Awaiting final plans for consultation submission.
  const humanReadableResponseEstimate = useMemo(() => getHumanReadableDate(submissionDetails.responseEstimateDateTime), [submissionDetails]);

  const [isConsultationSubmitted] = useState(!_isEmpty(submissionDetails));

  const dispatch = useDispatch();

  useEffect(() => {
    if ((product === PRODUCT.PRIMARY_CARE || product === PRODUCT.E_SPECIALIST) && _includes(ENDSTATES_WHICH_REQUIRE_SUBMISSION, endstate)) {
      if (isConsultationSubmitted) {
        dispatch({ type: STRINGS.CONSULTATION_REDUCER.DUPLICATE_SUBMISSION_ERROR });
      } else {
        dispatch(submitPrimaryCareConsultation());
      }
    }
    if (product === PRODUCT.E_TRIAGE && isConsultationEnded) {
      dispatch(submitETriageConsultation());
    }
    if (product === PRODUCT.E_TRIAGE && endstate === RESPONSE_TYPE.ERROR) {
      dispatch(logMessage(practice.practiceId, sessionId, 'ERROR', 'Red react error dialog shown to patient, redirecting to reception'));
    }
  }, [endstate, dispatch, isConsultationSubmitted, product, isConsultationEnded, practice, sessionId]);

  function renderEndstateBody() {
    if (product === PRODUCT.E_TRIAGE) {
      switch (endstate) {
        case RESPONSE_TYPE.ENDSTATE_NO_ADDRESS:
          return '';
        case RESPONSE_TYPE.ERROR:
          return <ErrorETriageModal isClinicalReviewMode={isClinicalReviewMode} error={error} />;
        default:
          return <SubmitETriage />;
      }
    }
    switch (endstate) {
      case RESPONSE_TYPE.ENDSTATE_COMPLETE:
      case RESPONSE_TYPE.ENDSTATE_COMPLETE_ADMIN:
      case RESPONSE_TYPE.ENDSTATE_COMPLETE_PATIENT_RESPONSE:
      case RESPONSE_TYPE.ENDSTATE_COMPLETE_AUTISM_PRE_HEALTH:
      case RESPONSE_TYPE.ENDSTATE_COMPLETE_PATIENT_REGISTRATION:
      case RESPONSE_TYPE.ENDSTATE_COMPLETE_OP_AANDA_MSK:
        return (
          <Complete
            text={question.text}
            formattedResponseDateTime={humanReadableResponseEstimate}
            isPopupSurveyAvailableForProduct={isPopupSurveyAvailableForProduct}
            product={product}
            conditionName={conditionName}
            consultationId={consultationId}
            practiceName={practice && practice.name}
          />
        );
      case RESPONSE_TYPE.ERROR:
        return <Error error={error} isClinicalReviewMode={isClinicalReviewMode} />;
      default:
        return (
          <Incomplete
            isClinicalReviewMode={isClinicalReviewMode}
            question={question}
            previous={previous}
            responseType={endstate}
            isLoading={isLoading}
            isForwardNavigation={isForwardNavigation}
          />
        );
    }
  }

  return (
    <EndstateContainer modifier={['fillHeight', 'flexFill']}>
      {renderEndstateBody()}
      <DuplicateSubmissionModal
        isOpen={isDuplicateSubmissionModalOpen}
        onSelectConfirm={onConfirmDuplicateSubmissionModal}
        onSelectCancel={onCloseDuplicateSubmissionModal}
      />
      <ConsultationEndedModal
        isOpen={isConsultationEndedModalOpen}
        onSelectConfirm={onConfirmConsultationEndedModal}
        onSelectCancel={onCloseConsultationEndedModal}
      />
    </EndstateContainer>
  );
}
